import React, { useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from '../ShutdownRitual.module.css';

interface CelebrationScreenProps {
  onComplete: () => void;
  completed: boolean;
}

// Confetti colors
const CONFETTI_COLORS = [
  '#38a169',
  '#68d391',
  '#ffd700',
  '#ff6b6b',
  '#4ecdc4',
  '#9f7aea',
  '#2b6cb0',
];

interface ConfettiParticle {
  id: number;
  x: number;
  y: number;
  color: string;
  size: number;
  delay: number;
  duration: number;
  rotation: number;
}

function generateConfetti(count: number): ConfettiParticle[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -10 - Math.random() * 20,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    size: 6 + Math.random() * 8,
    delay: Math.random() * 2,
    duration: 3 + Math.random() * 2,
    rotation: Math.random() * 360,
  }));
}

function ConfettiPiece({ particle }: { particle: ConfettiParticle }) {
  return (
    <motion.div
      className={styles.confettiParticle}
      style={{
        left: `${particle.x}%`,
        width: particle.size,
        height: particle.size,
        backgroundColor: particle.color,
        borderRadius: Math.random() > 0.5 ? '50%' : '2px',
      }}
      initial={{
        y: particle.y,
        rotate: particle.rotation,
        opacity: 1,
      }}
      animate={{
        y: '120vh',
        rotate: particle.rotation + 720,
        opacity: [1, 1, 0],
      }}
      transition={{
        duration: particle.duration,
        delay: particle.delay,
        ease: 'linear',
        repeat: Infinity,
        repeatDelay: Math.random() * 2,
      }}
    />
  );
}

export function CelebrationScreen({ onComplete, completed }: CelebrationScreenProps) {
  const confettiParticles = useMemo(() => generateConfetti(50), []);

  return (
    <div
      className={styles.celebrationContainer}
      role="dialog"
      aria-modal="true"
      aria-label="Shutdown complete celebration"
    >
      {/* Confetti */}
      <div className={styles.confettiContainer} aria-hidden="true">
        {confettiParticles.map((particle) => (
          <ConfettiPiece key={particle.id} particle={particle} />
        ))}
      </div>

      {/* Content */}
      <motion.div
        className={styles.celebrationContent}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Checkmark Circle */}
        <motion.div
          className={styles.checkmarkCircle}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2,
          }}
        >
          <motion.span
            className={styles.checkmark}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.3 }}
            aria-hidden="true"
          >
            +
          </motion.span>
        </motion.div>

        {/* Title */}
        <motion.h1
          className={styles.celebrationTitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.4 }}
        >
          Shutdown Complete!
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className={styles.celebrationSubtitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          Your mind is now free for rest and relaxation. See you tomorrow.
        </motion.p>

        {/* Done Button */}
        <AnimatePresence>
          {completed && (
            <motion.button
              className={styles.doneButton}
              onClick={onComplete}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 30 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200, damping: 20 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Close and return to main screen"
            >
              Done
            </motion.button>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default CelebrationScreen;
